import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import SensorModel from 'src/app/services/sensor.model';
import QueryParamsModel from 'src/app/services/queryParams.model';
import SensorTypeModel from 'src/app/services/sensorType.model';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import { QueryParamsService } from 'src/app/services/query-params.service';
import { SensorTypeService } from 'src/app/services/sensor-type.service';
import { SensorService } from 'src/app/services/sensor.service';

declare var $: any;

@Component({
  selector: 'app-sensors',
  templateUrl: './sensors.component.html',
  styleUrls: ['./sensors.component.scss']
})
export class SensorsComponent implements OnInit, OnDestroy {
  public sensors: SensorModel[] = [];
  public sensorsList: SensorModel[] = [];
  public sensor: SensorModel = new SensorModel();
  public filteredSensors: SensorModel[] = [];
  public sensorTypes: Array<SensorTypeModel> = [];
  public sensorType: SensorTypeModel;
  public queryParams: Array<QueryParamsModel> = [];
  public queryParam: QueryParamsModel;
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public sensorDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public activePageNo: number;
  public clearSearchInput: boolean = false;
  public registerForm: FormGroup;
  public submitted = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;
  public startIndex = 0;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  private scrollTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  private batchLoader: BatchLoader<any>;

  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public sensorService: SensorService,
    public queryParamsService: QueryParamsService,
    public sensorTypeService: SensorTypeService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService
  ) {
    this.sensor = new SensorModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      vendor_name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      qp_id: ['', [Validators.required]],
      sensor_type_id: ['', [Validators.required]]
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.sensors = [];
      this.sensorsList = [];
      this.filteredSensors = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.sensors = await this.sensorService.getAllTSensors();
      this.queryParams = await this.queryParamsService.getAllQueryParam();
      this.sensorTypes = await this.sensorTypeService.getAllSensorTypes();
      for (let i = 0; i < this.sensors.length; i++) {
        this.sensorType = this.getSensorTypeById(String(this.sensors[i].sensor_type_id));
        this.sensors[i].sensorType = this.sensorType;
        this.queryParam = this.getQueryParamById(String(this.sensors[i].qp_id));
        this.sensors[i].qp = this.queryParam;
      }
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSensors : this.sensors,
      (updatedList) => {
        this.sensorsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  get f() {
    return this.registerForm.controls;
  }

  public ngOnDestroy(): void {
    this.sensorService.cancelOnGoingHttpRequests();
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.sensorTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.sensors.filter(sensor =>
          sensor.name?.toLowerCase().includes(query) ||
          sensor.qp?.name?.toLowerCase().includes(query) ||
          sensor.vendor_name?.toLowerCase().includes(query) ||
          sensor.sensorType?.name?.toLowerCase().includes(query)
        )
      : this.sensors;
    this.filteredSensors = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.sensorsList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.sensor = new SensorModel();
    this.sensorDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  public async addUpdateSensor() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formData = this.registerForm.value;
    const loginUser = localStorage.getItem('LoginUserName');
    formData.created_name = loginUser;
    formData.updated_name = loginUser;
    try {
      this.sensorDoubleClick = true;
      if (this.submitButton) {
        await this.sensorService.create(formData);
        this.toastrService.success('Sensor created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSensor = { ...formData };
        delete updateSensor.id;
        await this.sensorService.updateOneByID(this.sensor.id, updateSensor);
        this.toastrService.success('Sensor updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.sensorDoubleClick = false;
    }
  }

  public getSensorById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.sensorDoubleClick = false;
    this.updateButton = true;
    const foundSensors = this.sensorsList.find(uom => uom?.id === id);
    this.sensor = foundSensors ? { ...foundSensors } : new SensorModel();
    if (this.sensor) {
      this.registerForm.patchValue({
        name: this.sensor.name,
        vendor_name: this.sensor.vendor_name,
        qp_id: this.sensor.qp?.id || this.sensor.qp_id,
        sensor_type_id: this.sensor.sensorType?.id || this.sensor.sensor_type_id
      });
    }
  }

  public getSensorTypeById(id: string) {
    this.sensorType = this.sensorTypes.find(sensorType => sensorType && String(sensorType.id) === String(id));
    return this.sensorType;
  }

  public getQueryParamById(id: string) {
    this.queryParam = this.queryParams.find(queryParam => queryParam && String(queryParam.id) === String(id));
    return this.queryParam;
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}

